import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Building2 } from "lucide-react";
import { fmtDate } from "@/lib/constants";

const EMPTY = { name: "", industry: "", website: "", phone: "", email: "", address: "", notes: "", type: "customer" };

export default function Companies() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["companies", search, typeFilter],
    queryFn: async () => {
      const params = {};
      if (search) params.search = search;
      if (typeFilter !== "all") params.type = typeFilter;
      return (await api.get("/companies", { params })).data;
    },
  });

  const save = useMutation({
    mutationFn: async (body) => {
      if (body.id) return (await api.patch(`/companies/${body.id}`, body)).data;
      return (await api.post("/companies", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["companies"] });
      setOpen(false); setEditing(null);
      toast.success("Saved");
    },
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/companies/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["companies"] }); toast.success("Deleted"); },
  });

  const onNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const onEdit = (c) => { setEditing({ ...c }); setOpen(true); };

  return (
    <div>
      <PageHeader
        title="Companies"
        subtitle="Customers, leads & accounts."
        breadcrumb={["CRM", "Companies"]}
        actions={
          <Button data-testid="new-company-btn" onClick={onNew} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New company
          </Button>
        }
      />

      <div className="px-6 lg:px-10 py-6">
        <Card className="rounded-sm border-slate-200 industrial-shadow">
          <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-200 bg-slate-50/50">
            <div className="relative flex-1 max-w-md">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="company-search"
                placeholder="Search companies..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 rounded-sm"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="company-type-filter" className="w-40 h-10 rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="lead">Leads</SelectItem>
                <SelectItem value="supplier">Suppliers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left stamp-label text-slate-500">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Industry</th>
                  <th className="px-4 py-3 hidden md:table-cell">Phone</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Email</th>
                  <th className="px-4 py-3 hidden lg:table-cell">Created</th>
                  <th className="px-4 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {companies.map((c) => (
                  <tr key={c.id} className="hover:bg-cyan-50/30" data-testid={`company-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-100 flex items-center justify-center text-slate-700"><Building2 size={14} /></div>
                        <div className="font-medium text-slate-900">{c.name}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="rounded-sm border-slate-300 bg-white">{c.type}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.industry || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-slate-500 hidden lg:table-cell">{fmtDate(c.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onEdit(c)} data-testid={`edit-company-${c.id}`} className="h-8 w-8 p-0"><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => del.mutate(c.id)} data-testid={`delete-company-${c.id}`} className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {companies.length === 0 && !isLoading && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No companies found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl" data-testid="company-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.id ? "Edit company" : "New company"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Name" required>
                  <Input data-testid="company-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </Field>
                <Field label="Type">
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="supplier">Supplier</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Industry"><Input value={editing.industry || ""} onChange={(e) => setEditing({ ...editing, industry: e.target.value })} /></Field>
                <Field label="Website"><Input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} /></Field>
                <Field label="Phone"><Input value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></Field>
                <Field label="Email"><Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
              </div>
              <Field label="Address"><Input value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></Field>
              <Field label="Notes"><Textarea rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-company-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function Field({ label, required, children }) {
  return (
    <div>
      <Label className="stamp-label text-slate-600">{label}{required && <span className="text-cyan-600 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
